/** @format */

import { PROPAGATE_ERROR_TO_SERVER } from './../../server/propagate-error-to-server';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import {
	setDisplayableProductListData,
	setAllFilters,
	setInitialPageNumber,
	setInitialPageSize,
	setIsProductListPageLoading,
	setIsProductListPageValid,
	setPinnedContent,
	setPiqProducts,
	setProductList,
	setProductListSEO,
	setSelectedSort,
	productListResolverFeatureFlagsSet,
	setCriteoProducts
} from '@plp/data-access/state';
import {
	ProductListApiService,
	ProductListCompareService,
	ProductListMlService,
	ProductListUIService
} from '@plp/data-access/services';
import { ProductList } from '@plp/data-access/interfaces';
import { catchError, filter, mergeMap, take, tap } from 'rxjs/operators';

import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	Inject,
	Injectable,
	makeStateKey,
	Optional,
	PLATFORM_ID,
	StateKey,
	TransferState
} from '@angular/core';
import { Logger } from '@logger';
import { SeoService } from '@seo/data-access';
import { Store } from '@ngrx/store';
import { CriteoService, KillswitchService } from '@shared/data-access/services';
import { Observable, of } from 'rxjs';
import { setCommonPageType } from '@state/data-access';
import { familyPageDropdownValues, plpDefaultSortMap } from '@plp/data-access/constants';
import { ProductListSEO, ProductTypes } from '@shared/data-access/interfaces';

@Injectable()
export class ProductListPageResolveGuard {
	showPinnedContent = true;
	showPinnedProducts = true;
	showPLPSponsoredProducts = false;
	isUsingCriteoSPAAdServer = false;

	constructor(
		private readonly log: Logger,
		private readonly seoService: SeoService,
		private readonly productListUIService: ProductListUIService,
		private readonly productListApiService: ProductListApiService,
		private readonly killSwitchService: KillswitchService,
		private readonly store: Store,
		private readonly state: TransferState,
		private readonly plpCompareService: ProductListCompareService,
		private readonly plpMlService: ProductListMlService,
		private readonly criteoService: CriteoService,
		@Inject(PLATFORM_ID) private readonly platformId: string,
		@Optional() @Inject(PROPAGATE_ERROR_TO_SERVER) private readonly propagateToServer
	) {
		this.store.dispatch(setCommonPageType({ pageType: 'plp' }));
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((data) => {
				this.store.dispatch(productListResolverFeatureFlagsSet({ featureFlags: data }));
				this.showPinnedContent = data.showPinnedContent;
				this.showPinnedProducts = data.showPinnedProducts;
				this.showPLPSponsoredProducts = data.showPLPSponsoredProducts;
				this.isUsingCriteoSPAAdServer = data.isUsingCriteoSPAAdServer;
			});
	}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		return this.seoService.getSEOData$(route.params['seoToken'], 1, true).pipe(
			mergeMap((productListSEO: { categories: ProductListSEO[] }) => {
				// keep the loading spinner off on PLP, just reserved for SRLP.
				this.store.dispatch(
					setIsProductListPageLoading({ isProductListPageLoading: false })
				);

				if (typeof productListSEO !== 'undefined' && Object.keys(productListSEO).length) {
					if (productListSEO?.categories?.length) {
						this.handlePageRedirect(productListSEO);

						this.store.dispatch(
							setProductListSEO({ productListSEO: productListSEO.categories[0] })
						);

						this.getDisplayableProductListDataFromState();

						// Only execute in the Server to avoid duplicated tags
						if (isPlatformServer(this.platformId)) {
							this.seoService.initializePageSEO(
								productListSEO,
								state.url.split('?')[0],
								'family'
							);
						}

						// MAKE CALL FOR PRODUCT LIST DATA
						return this.makeCallForProductListData(
							productListSEO.categories[0].catgroupId,
							route.params['seoToken'],
							route.queryParams,
							productListSEO.categories[0]
						);
					} else {
						this.store.dispatch(
							setIsProductListPageValid({ isProductListPageValid: false })
						);
					}
				}

				return of(null);
			}),
			catchError((error) => {
				if (isPlatformServer(this.platformId) && error?.errorType === 'redirect') {
					throw error;
				}

				this.store.dispatch(
					setIsProductListPageLoading({ isProductListPageLoading: false })
				);
				this.store.dispatch(setIsProductListPageValid({ isProductListPageValid: false }));

				this.log.error(
					`APPLICATION_ERROR:HTTP_ERROR: Error getting page by SEO TOKEN: ${route.params['seoToken']}. ${this.getErrorToPrint(error)}`
				);

				return of(null);
			})
		);
	}

	/**
	 * Handles the redirection of the page based on the product list SEO information.
	 * If the page type is 'c' (category) or 's' (static) or 'f' (family), it redirects to the corresponding SEO URL.
	 * Otherwise, it dispatches an action to set the product list page as valid.
	 *
	 * @param productListSEO - The SEO information of the product list, which includes categories with page type and SEO URL.
	 */
	handlePageRedirect(productListSEO: any): void {
		if (
			isPlatformBrowser(this.platformId) ||
			!productListSEO.categories[0].pageType ||
			!productListSEO.categories[0].seoUrl
		) {
			return;
		}

		const pageType = productListSEO.categories[0].pageType;
		const seoUrl = productListSEO.categories[0].seoUrl;
		const isRedirect = productListSEO.categories[0].redirect;
		if (seoUrl && ((pageType === 'f' && isRedirect) || pageType === 'c' || pageType === 's')) {
			this.propagateToServer({
				errorType: 'redirect',
				redirectUrl: `/${pageType}/${seoUrl}`
			});
			throw { errorType: 'redirect', redirectUrl: `/${pageType}/${seoUrl}` };
		}
		this.store.dispatch(setIsProductListPageValid({ isProductListPageValid: true }));
	}

	/**
	 * Retrieves the displayable product list data from the state and dispatches it to the store.
	 * If the data is found in the state, it dispatches an action to set the displayable product list data
	 * and then removes the data from the state.
	 *
	 * @returns {void}
	 */
	private getDisplayableProductListDataFromState(): void {
		const DISPLAYABLE_PRODUCT_LIST_DATA_KEY: StateKey<any> = makeStateKey(
			'DISPLAYABLE_PRODUCT_LIST_DATA'
		);
		const dpld = this.state.get(DISPLAYABLE_PRODUCT_LIST_DATA_KEY, null);
		if (dpld) {
			this.store.dispatch(
				setDisplayableProductListData({ displayableProductListData: dpld })
			);
			this.state.remove(DISPLAYABLE_PRODUCT_LIST_DATA_KEY);
		}
	}

	/**
	 * @description
	 * Accepts an error and stringifies the important parts
	 */
	private getErrorToPrint(error: any): string {
		try {
			return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
		} catch (error) {
			// Swallow the JSON stringify error
			return '';
		}
	}

	/**
	 * @description
	 * Make call for Product List for the PLP product Grid once the seo data is set
	 */
	private makeCallForProductListData(
		catgroupId: number,
		seoUrlToken: string,
		urlQueryParams: Params,
		categories: ProductListSEO
	): Observable<ProductList | null> {
		const pageNumber = this.productListUIService.getPageNumber(urlQueryParams?.pageNumber);
		this.store.dispatch(setInitialPageNumber({ pageNumber }));

		const pageSize = this.productListUIService.getPageSize(urlQueryParams?.pageSize);
		this.store.dispatch(setInitialPageSize({ pageSize }));

		const selectedFilters =
			this.productListApiService.formatFiltersFromURL(urlQueryParams.filterFacets) ?? {};
		this.store.dispatch(setAllFilters({ selectedFilters }));

		this.getAndSetSelectedSort(categories, urlQueryParams?.selectedSort);

		const queryParams = this.productListApiService.setProductListParamDataForApi(
			catgroupId,
			null,
			pageNumber,
			selectedFilters,
			pageSize
		);
		return this.productListApiService
			.getProductListDataFromApi$(queryParams, 1, this.showPinnedProducts)
			.pipe(
				take(1),
				filter(
					(productList: ProductList | null) =>
						typeof productList !== 'undefined' && productList !== null
				),
				tap((productList: ProductList | null) => {
					this.plpMlService.generateNewMlSearchEventParentUUID();

					this.store.dispatch(setProductList({ productList }));
					if (!this.plpCompareService.hasTheComparePaintedDoorTestBeenOpened()) {
						this.plpCompareService.getCompareListFromSessionStorage();
					}
					if (this.isUsingCriteoSPAAdServer) {
						this.getAndSetCriteoProducts(
							categories,
							urlQueryParams?.pageNumber,
							urlQueryParams?.pageSize,
							selectedFilters
						);
					} else if (this.showPLPSponsoredProducts) {
						// Fallback to PromoteIQ
						this.getAndSetPromoteIQProducts(categories, productList, catgroupId);
					} else {
						this.store.dispatch(setPiqProducts({ piqProducts: [] }));
					}
					this.getAndSetPinnedContent(productList, seoUrlToken);
				}),
				catchError((error) => {
					this.log.error(
						`Search API error for category ID ${catgroupId} with the following error: ${this.getErrorToPrint(error)}`
					);

					const pl = this.getProductListFromState();
					if (pl) {
						this.store.dispatch(setProductList({ productList: pl }));
					}

					return of(null);
				})
			);
	}

	/**
	 * Retrieves the product list data from the application state.
	 *
	 * This method searches through the state object for a specific key that contains
	 * the 'searchVO' string within its 'u' property. Once found, it extracts and returns
	 * the associated 'b' property value as the product list data.
	 */
	private getProductListFromState(): ProductList | null {
		const state = this.state['store'];
		let productListData = null;

		for (const val of Object.values(state) as any) {
			if (val.u?.includes('searchVO')) {
				productListData = val.b;
				break;
			}
		}

		return productListData;
	}

	private getAndSetPromoteIQProducts(
		categories,
		productListData: ProductList,
		catgroupId: number
	): void {
		if (
			this.showPLPSponsoredProducts &&
			(categories?.attr['SPONSORED_PRODUCTS'] === undefined ||
				categories?.attr['SPONSORED_PRODUCTS']?.value === 'Y')
		) {
			this.productListUIService.setPromoteIQProductsToState(productListData, catgroupId);
			return;
		}

		this.store.dispatch(setPiqProducts({ piqProducts: [] }));
	}

	private getAndSetCriteoProducts(
		categories: ProductListSEO,
		pageNumber: number,
		listSize: number,
		filters = null
	): void {
		const formattedBreadcrumb = this.criteoService.transformToCriteoBreadcrumbFormat(
			categories.attr.BREADCRUMB.value
		);
		const criteoFilters =
			filters && Object.keys(filters).length > 0
				? this.criteoService.parseFilters(filters)
				: null;
		this.criteoService
			.viewProductsPage({
				productType: ProductTypes.VIEW_CATEGORY,
				category: formattedBreadcrumb,
				listSize,
				pageNumber,
				filters: criteoFilters
			})
			.subscribe({
				next: (res) => {
					this.criteoService.fireBeacon(res?.placements[0]?.carousel[0]?.OnLoadBeacon);
					this.store.dispatch(setCriteoProducts({ criteoProducts: res }));
				},
				error: (err) => {
					console.error('Error fetching Criteo data:', err);
				}
			});
		this.store.dispatch(setCriteoProducts({ criteoProducts: null }));
	}

	private getAndSetPinnedContent(productListData: ProductList, seoUrlToken: string): void {
		if (this.showPinnedContent) {
			this.productListUIService.setPinnedContentHomrDataToState(productListData, seoUrlToken);
			return;
		}

		this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
	}

	private getAndSetSelectedSort(
		productListSEO: ProductListSEO,
		qpSelectedSort: string | undefined
	): void {
		const defaultSortMap = plpDefaultSortMap;

		const sortDropdownValues = familyPageDropdownValues;

		const queryParamSortToNumber: number = qpSelectedSort ? parseInt(qpSelectedSort, 10) : 0;
		const sortBasedOnQueryParam = {
			label: sortDropdownValues[queryParamSortToNumber]?.label,
			value: queryParamSortToNumber
		};

		const defaultSortFromSEO: number = defaultSortMap[productListSEO.longDescription];
		const sortBasedOnSEOData = {
			label: sortDropdownValues[defaultSortFromSEO]?.label,
			value: defaultSortFromSEO
		};

		const selectedSort: { label: string; value: number } = qpSelectedSort
			? sortBasedOnQueryParam
			: sortBasedOnSEOData;

		this.store.dispatch(setSelectedSort({ selectedSort }));
	}
}
